// composeRecipePatch — the FORWARD half of a Quick-Style application as a
// pure async function. It produces everything needed to paint a style recipe
// onto a fabric Textbox WITHOUT touching an object: the resolved fill paint,
// the accumulated effect prop patch (each EFFECT_DEFS derive over merged
// defaults, with slot displacement + hollow's fill→stroke routing), and the
// composed highlight rgba.
//
// The ENGINE (useCanvasEditor.js applyTextStyle) and the style-preview
// renderer (stylePreviewRenderer.js) both call this, so thumbnails can never
// drift from what a click paints on the canvas — the same guarantee
// effectDefs already gives the panel, extended to fill resolution.
//
// Recipe shape (textPresets.js):
//   { fill?: FillValue, effects?: { shadow?: {id,params}, stroke?: {id,params} }, highlight?: { color, fade } }
//
// This is the forward path only: it assumes a VIRGIN slot state (the engine
// runs clearTextEffects first; the preview starts from a fresh Textbox).
// Slot-displacement WITHIN the recipe (e.g. an effect entry that claims an
// already-filled slot, sticker's dual-slot atomicity) is replicated exactly.

import {
  EFFECT_DEFS,
  SLOT_RESET,
  effectDefaults,
  rgba,
  solidFromFabricFill,
} from './effectDefs';
import { textureUrl } from './fillPresets';
import { resolveFill } from '../../../../../posterTools';

// Print-sharpness base for texture tiles — mirror of useCanvasEditor.js's
// TEXTURE_PRINT_BASE_SCALE (the 72/300 texture scaling law). Keep in sync.
export const TEXTURE_PRINT_BASE_SCALE = 72 / 300;

/**
 * Resolve a recipe FillValue into fabric paint. Manifest texture recipes
 * store an ID (portability law) — the URL is re-derived here via textureUrl;
 * user-imported textures (PaintPicker v2) store a data-URL `src`, valid in
 * every deployment. Print-base scale applies to both, exactly like the
 * engine's resolveTextFillPaint (keep the two in sync).
 * Rejects on a dead texture URL (callers fall back to a solid).
 */
export function resolveRecipeFillPaint(fillValue) {
  if (fillValue?.type === 'texture' && (fillValue.id || fillValue.src)) {
    return resolveFill({
      type: 'texture',
      url: fillValue.src ?? textureUrl(fillValue.id),
      scale: (fillValue.scale ?? 1) * TEXTURE_PRINT_BASE_SCALE,
    });
  }
  return resolveFill(fillValue);
}

/** First solid color hiding in a FillValue recipe (string | solid | gradient)
 * — mirror of the engine's solidFromFillValue. Textures yield null. */
export function solidFromFillValue(fillValue) {
  if (typeof fillValue === 'string') return fillValue;
  if (fillValue?.type === 'solid') return fillValue.color;
  return fillValue?.stops?.[0]?.color ?? null;
}

/**
 * A recipe's effect entries in engine application order: Object.values
 * insertion order over recipe.effects, filtered by the same `entry?.id`
 * guard applyTextStyle uses. (Unknown effect ids survive this filter and are
 * skipped later, matching applyTextEffect's `!def` early return.)
 */
export function recipeSlotEntries(recipe) {
  return Object.values(recipe?.effects || {}).filter((e) => e?.id);
}

/**
 * Compose the full paint patch for a style recipe.
 *
 * @param recipe   textPresets-shaped recipe ({ fill?, effects?, highlight? }).
 * @param options
 *   fontSize     — the target object's fontSize (all derives are
 *                  fontSize-relative). Default 40.
 *   ctx          — derive context when the recipe brings no fill of its own:
 *                  { fillColor } (the engine passes textEffectCtx(object);
 *                  the preview passes its ground-appropriate default).
 *   currentFill  — the FillValue the object would fall back to for hollow's
 *                  stroke routing when the recipe has no (or a dead) fill.
 *                  The engine passes data.textFill ?? fillValueFromLive(fill);
 *                  the preview passes its default solid.
 *   lastSolid    — data.textFillLastSolid equivalent, used only in the
 *                  displaced-hollow restore corner (mirrors restoreTextFill).
 *
 * @returns {Promise<{
 *   props: object,              // effect prop patch: slot resets + derives,
 *                               // incl. hollow's fill:'transparent' + stroke paint.
 *                               // Apply AFTER the base fill paint.
 *   effects: object|null,       // data.textEffects value ({slot: {id, params}}
 *                               // with sticker sharing ONE entry across both
 *                               // slots) — null when no valid effect ran.
 *   effectsTouched: boolean,    // at least one valid effect was composed
 *   fill: {                     // the resolved fill half
 *     value,                    //   the recipe FillValue (or null)
 *     paint,                    //   resolved fabric paint (string|Gradient|Pattern)
 *     applied: boolean,         //   true → set object.fill = paint
 *     failed: boolean,          //   dead texture (engine keeps current fill + toasts)
 *     becomingSolid: boolean,   //   for the engine's textFillLastSolid capture
 *   },
 *   fillPaint,                  // convenience: fill.applied ? fill.paint : undefined
 *   strokeFillRouted: boolean,  // a fillRouting ('hollow') effect ran — the
 *                               // engine captures data.textFill when null
 *   highlight: object|null,     // data.highlight value ({color, fade} | null)
 *   textBackgroundColor: string,// composed rgba ('' clears)
 *   fontSize: number,           // the fontSize the derives ran at
 * }>}
 */
export async function composeRecipePatch(recipe, { fontSize = 40, ctx, currentFill = null, lastSolid = null } = {}) {
  const props = {};
  const effects = {};
  let effectsTouched = false;
  let strokeFillRouted = false;

  // ---- fill half (the resolve part of setTextFill) ----
  const fill = {
    value: recipe?.fill ?? null,
    paint: undefined,
    applied: false,
    failed: false,
    becomingSolid: typeof recipe?.fill === 'string' || recipe?.fill?.type === 'solid',
  };
  if (recipe?.fill) {
    try {
      fill.paint = await resolveRecipeFillPaint(recipe.fill);
    } catch {
      fill.failed = true; // dead texture — the engine keeps the current fill
    }
    if (!fill.failed && fill.paint !== undefined) fill.applied = true;
  }

  // What the effects half sees as data.textFill: the recipe's fill once
  // applied, else the object's pre-existing / live-derived recipe.
  const effectiveFillValue = fill.applied ? recipe.fill : currentFill;

  // Live-fill tracker mirroring textEffectCtx(object) between engine
  // mutations: starts at the applied paint (or the caller's collapsed ctx
  // color) and goes 'transparent' when hollow routes the paint away.
  let liveFill = fill.applied ? fill.paint : (ctx?.fillColor ?? null);
  const deriveCtx = () => {
    const liveSolid =
      (typeof liveFill === 'string' && liveFill !== 'transparent') || liveFill?.colorStops?.length
        ? solidFromFabricFill(liveFill)
        : null;
    return { fillColor: liveSolid ?? solidFromFillValue(effectiveFillValue) ?? ctx?.fillColor ?? '#000000' };
  };

  // ---- effects half (the derive part of the applyTextEffect sequence) ----
  let strokeRoutedPaint; // hollow's resolved stroke paint, reused by restore
  for (const entry of recipeSlotEntries(recipe)) {
    const def = EFFECT_DEFS[entry.id];
    if (!def) continue; // applyTextEffect's unknown-id guard
    const merged = { ...effectDefaults(def), ...entry.params };

    // Displace an occupant of a claimed slot with its OWN slot resets first
    // (tile-swap semantics; sticker spans shadow+stroke atomically, so
    // displacing it clears both).
    for (const slot of def.slots) {
      const occupant = effects[slot];
      if (!occupant?.id || occupant.id === def.id) continue;
      const occDef = EFFECT_DEFS[occupant.id];
      (occDef?.slots || [slot]).forEach((s) => {
        if (SLOT_RESET[s]) Object.assign(props, SLOT_RESET[s]);
        effects[s] = null;
      });
      if (occDef?.fillRouting === 'stroke') {
        // Displaced hollow held the fill hostage — restore it (mirrors
        // restoreTextFill's paint ?? solid ?? lastSolid ?? '#000000' chain).
        const restored = fill.applied
          ? fill.paint
          : (strokeRoutedPaint ?? solidFromFillValue(effectiveFillValue) ?? lastSolid ?? '#000000');
        props.fill = restored;
        liveFill = restored;
      }
    }

    const derived = def.derive(merged, fontSize, deriveCtx());
    Object.assign(props, derived);
    if ('fill' in derived) liveFill = derived.fill; // hollow: fill → 'transparent'

    if (def.fillRouting === 'stroke') {
      // Hollow paints the FILL RECIPE onto the stroke (gradients/textures
      // outline natively); a dead texture falls back to a solid.
      strokeFillRouted = true;
      if (fill.applied) {
        strokeRoutedPaint = fill.paint;
      } else {
        try {
          strokeRoutedPaint = await resolveRecipeFillPaint(effectiveFillValue);
        } catch {
          strokeRoutedPaint = undefined;
        }
      }
      props.stroke = strokeRoutedPaint ?? solidFromFillValue(effectiveFillValue) ?? '#000000';
    } else if (def.strokeFillParam) {
      // The effect's OWN stroke paint (sticker's border), independent of the
      // text fill — mirror of the engine's strokeFill branch in
      // applyTextEffect, so a Styles thumbnail shows a textured border
      // truthfully instead of the solid derive left out.
      // Solids/gradients already arrived via derive; only a texture is absent,
      // because derive omits the key rather than await an image decode.
      const value = merged[def.strokeFillParam];
      if (value?.type === 'texture' && value.id) {
        let paint;
        try {
          paint = await resolveRecipeFillPaint(value);
        } catch {
          paint = undefined; // dead texture → solid fallback below
        }
        // Never leave the border unpainted (a texture carries no color).
        props.stroke = paint ?? solidFromFillValue(value) ?? '#ffffff';
      }
    }

    // One shared entry object per effect — sticker's dual slots point at the
    // SAME entry, exactly like the engine's data bag.
    const slotEntry = { id: def.id, params: merged };
    def.slots.forEach((s) => { effects[s] = slotEntry; });
    effectsTouched = true;
  }

  // ---- highlight (the compose part of setTextHighlight) ----
  const highlight = recipe?.highlight?.color
    ? { color: recipe.highlight.color, fade: recipe.highlight.fade ?? 0 }
    : null;
  const textBackgroundColor = highlight
    ? rgba(highlight.color, 1 - highlight.fade / 100)
    : '';

  return {
    props,
    effects: effectsTouched ? effects : null,
    effectsTouched,
    fill,
    fillPaint: fill.applied ? fill.paint : undefined,
    strokeFillRouted,
    highlight,
    textBackgroundColor,
    fontSize,
  };
}
